/**
 * Lockstep Platform SDK for TypeScript
 *
 * (c) 2021-2023 Lockstep, Inc.
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 *
 * @author     Lockstep Network <support@lockstep.io>
 * @copyright  2021-2023 Lockstep, Inc.
 * @link       https://github.com/Lockstep-Network/lockstep-sdk-typescript
 */
export class ContactsClient {
    /**
     * Internal constructor for this client library
     */
    constructor(client) {
        this.client = client;
    }
    /**
     * Retrieves the Contact specified by this unique identifier, optionally including nested data sets.
     *
     * A Contact contains information about a person or role within a Company. You can use Contacts to track information about who is responsible for a specific project, who handles invoices, or information about which role at a particular customer or vendor you should speak with about invoices.
     *
     * @param id The unique Lockstep Platform ID number of this Contact; NOT the customer's ERP key
     * @param include To fetch additional data on this object, specify the list of elements to retrieve. Available collections: Attachments, CustomFields, Notes
     */
    retrieveContact(id, include) {
        const url = `/api/v1/Contacts/${id}`;
        const options = {
            params: {
                include,
            },
        };
        return this.client.request("get", url, options, null);
    }
    /**
     * Updates a contact that matches the specified id with the requested information.
     *
     * The PATCH method allows you to change specific values on the object while leaving other values alone.  As input you should supply a list of field names and new values.  If you do not provide the name of a field, that field will remain unchanged.  This allows you to ensure that you are only updating the specific fields desired.
     *
     * A Contact contains information about a person or role within a Company. You can use Contacts to track information about who is responsible for a specific project, who handles invoices, or information about which role at a particular customer or vendor you should speak with about invoices.
     *
     * @param id The unique Lockstep Platform ID number of the Contact to update; NOT the customer's ERP key
     * @param body A list of changes to apply to this Contact
     */
    updateContact(id, body) {
        const url = `/api/v1/Contacts/${id}`;
        return this.client.request("patch", url, null, body);
    }
    /**
     * Delete the Contact referred to by this unique identifier.
     *
     * A Contact contains information about a person or role within a Company. You can use Contacts to track information about who is responsible for a specific project, who handles invoices, or information about which role at a particular customer or vendor you should speak with about invoices.
     *
     * @param id The unique Lockstep Platform ID number of the Contact to delete; NOT the customer's ERP key
     */
    deleteContact(id) {
        const url = `/api/v1/Contacts/${id}`;
        return this.client.request("delete", url, null, null);
    }
    /**
     * Creates one or more contacts from a given model.
     *
     * A Contact contains information about a person or role within a Company. You can use Contacts to track information about who is responsible for a specific project, who handles invoices, or information about which role at a particular customer or vendor you should speak with about invoices.
     *
     * @param body The Contacts to create
     */
    createContacts(body) {
        const url = `/api/v1/Contacts`;
        return this.client.request("post", url, null, body);
    }
    /**
     * Delete the Contacts referred to by these unique identifiers.
     *
     * A Contact contains information about a person or role within a Company. You can use Contacts to track information about who is responsible for a specific project, who handles invoices, or information about which role at a particular customer or vendor you should speak with about invoices.
     *
     * @param body The unique Lockstep Platform ID numbers of the Contacts to delete; NOT the customer's ERP keys
     */
    deleteContacts(body) {
        const url = `/api/v1/Contacts`;
        return this.client.request("delete", url, null, body);
    }
    /**
     * Queries Contacts for this account using the specified filtering, sorting, nested fetch, and pagination rules requested.
     *
     * More information on querying can be found on the [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight) page on the Lockstep Developer website.
     *
     * A Contact contains information about a person or role within a Company. You can use Contacts to track information about who is responsible for a specific project, who handles invoices, or information about which role at a particular customer or vendor you should speak with about invoices.
     *
     * @param filter The filter for this query. See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param include To fetch additional data on this object, specify the list of elements to retrieve. Available collections: Attachments, CustomFields, Notes
     * @param order The sort order for this query. See See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageSize The page size for results (default 250, maximum of 500). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageNumber The page number for results (default 0). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     */
    queryContacts(filter, include, order, pageSize, pageNumber) {
        const url = `/api/v1/Contacts/query`;
        const options = {
            params: {
                filter,
                include,
                order,
                pageSize,
                pageNumber,
            },
        };
        return this.client.request("get", url, options, null);
    }
}
//# sourceMappingURL=ContactsClient.js.map